import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'

import { ApiError, isApiError } from '@/api'
import { getNextInvoice, NextInvoiceResponse, switchEnterprisePlan, SwitchPlanRequest } from '@/api/billing'
import {
  connectSSO,
  createEnterpriseNew,
  CreateEnterpriseRequestNew,
  getMySeatInfo,
  getUserEnterprise,
  MySeatInfoResponse,
  setupSCIM,
  setupSSO,
  updateEnterprise,
  UpdateEnterpriseRequest,
  UpdateEnterpriseResponse,
  updateEnterpriseSettings,
  UpdateEnterpriseSettingsRequest,
} from '@/api/enterprise'
import { BillingProvider } from '@/api/generated/api'
import { getSubscription, SubscriptionResponse } from '@/api/user'
import { RootState } from '@/store'
import {
  ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES,
  Enterprise,
  EnterpriseSettings,
  EnterpriseSubscription,
  PaymentSchedule,
  PlanType,
  SubscriptionStatus,
} from '@/types'
import { calculateTrialDays } from '@/utils/dates'
import { isAdminRole } from '@/utils/role'
import { getPlanTypeFromProductName, gracePeriodDaysLeft, isInGracePeriod } from '@/utils/subscription'

export interface UserState {
  userId: string | null
  enterprise: Enterprise | null
  // Must not persist — must start false on every cold load so the gate fires.
  enterpriseLoaded: boolean
  // Distinguishes "loaded, no enterprise" (authoritative null → onboarding) from
  // "fetch failed" (transient → don't misroute the user to /get-started).
  enterpriseError: boolean
  avatarUrl: string | null
  isLoading: boolean
  email: string
  name: string
  subscription: SubscriptionData | null
  // SSO state
  ssoProvider: string | null
  isSSOLoading: boolean
  isSSOConnected: boolean
  isSSOValidating: boolean
  // Billing state
  nextInvoice: NextInvoiceResponse | null
  isNextInvoiceLoading: boolean
  // Seat info state
  seatInfo: MySeatInfoResponse | null
  isSeatInfoLoading: boolean
}

// Single source of truth for fields that carry tenant-scoped data. The persist
// blacklist in store/index.ts derives from this list, and `setUser`'s payload
// type forbids these keys so a future caller can't silently leak them back in.
export const TENANT_FIELDS = [
  'enterprise',
  'enterpriseLoaded',
  'enterpriseError',
  'isSSOConnected',
  'isSSOValidating',
  'nextInvoice',
  'seatInfo',
  'ssoProvider',
  'subscription',
] as const satisfies readonly (keyof UserState)[]
export type TenantField = (typeof TENANT_FIELDS)[number]

interface SubscriptionData {
  status: string
  trial_ends_at: number
  created_at: number
  plan: string
  credits: number
  is_subscribed: boolean
  renews_at: number
  team_domain_status: string
  is_student: boolean
  total_trial_days: number
  source_of_subscription?: string
  team_trial_given?: boolean
  most_recent_enterprise_id?: string | null
  most_recent_enterprise_rejoinable?: boolean
  grace_period_ends_at?: number | null
}

interface ConnectSSOResponse {
  message: string
  detail?: string
  provider?: string
}

const isSsoInactiveResponse = (error: ApiError): boolean => {
  const message = `${error.message} ${error.detail ?? ''}`.toLowerCase()
  return message.includes('not active') || message.includes('inactive')
}

const initialState: UserState = {
  userId: null,
  enterprise: null,
  enterpriseLoaded: false,
  enterpriseError: false,
  avatarUrl: null,
  isLoading: false,
  email: '',
  name: '',
  subscription: null,
  // SSO state
  ssoProvider: null,
  isSSOLoading: false,
  isSSOConnected: false,
  isSSOValidating: false,
  // Billing state
  nextInvoice: null,
  isNextInvoiceLoading: false,
  // Seat info state
  seatInfo: null,
  isSeatInfoLoading: false,
}

// Helper to normalize portal URLs returned from the backend
const cleanPortalUrl = (url: string): string => {
  let cleanedUrl = url

  // Clean the URL if it contains 'link=' prefix
  if (cleanedUrl.startsWith('link=')) {
    cleanedUrl = cleanedUrl.substring(5)
  }

  // Remove any surrounding quotes if present
  if (
    (cleanedUrl.startsWith("'") && cleanedUrl.endsWith("'")) ||
    (cleanedUrl.startsWith('"') && cleanedUrl.endsWith('"'))
  ) {
    cleanedUrl = cleanedUrl.substring(1, cleanedUrl.length - 1)
  }

  return cleanedUrl
}

export const updateEnterpriseThunk = createAsyncThunk(
  'user/updateEnterprise',
  async (data: UpdateEnterpriseRequest, { rejectWithValue }) => {
    try {
      const response = await updateEnterprise(data)
      return response.data
    } catch (error) {
      const message = 'Failed to update organization'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const createEnterpriseThunk = createAsyncThunk(
  'user/createEnterprise',
  async (data: CreateEnterpriseRequestNew, { rejectWithValue }) => {
    try {
      const response = await createEnterpriseNew(data)
      return response.data
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const e = error as any
      const message = e?.detail ?? e?.response?.data?.detail ?? e?.message ?? 'Failed to create organization'

      console.error('Create enterprise error:', message, error)
      return rejectWithValue(message)
    }
  }
)

export const getEnterpriseThunk = createAsyncThunk(
  'user/getEnterprise',
  async (params: { needSubscription: boolean }, { rejectWithValue }) => {
    try {
      let response = await getUserEnterprise()
      let data = response.data
      // If we need a subscription, but don't have one, refetch after a short delay
      if (params.needSubscription && !data.subscription) {
        await new Promise((resolve) => setTimeout(resolve, 5000))
        response = await getUserEnterprise()
        data = response.data
      }
      return data
    } catch (error) {
      const message = 'Failed to get organization'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

export const updateEnterpriseSettingsThunk = createAsyncThunk(
  'user/updateEnterpriseSettings',
  async (data: UpdateEnterpriseSettingsRequest, { rejectWithValue }) => {
    try {
      const response = await updateEnterpriseSettings(data)
      return response.data
    } catch (error) {
      const message = 'Failed to update organization settings'
      console.error(message, error)
      return rejectWithValue(message)
    }
  }
)

// Distinct from updateEnterpriseSettingsThunk so the admin UI can surface the
// backend's specific validation message inline instead of the generic settings-failure copy.
export const updateAppDenyListThunk = createAsyncThunk(
  'user/updateAppDenyList',
  async (data: UpdateEnterpriseSettingsRequest, { rejectWithValue }) => {
    try {
      const response = await updateEnterpriseSettings(data)
      return response.data
    } catch (error) {
      console.error('Failed to update app deny list', error)
      return rejectWithValue(isApiError(error) ? error.message : "Couldn't update blocked apps. Try again.")
    }
  }
)

// Distinct thunk so the admin UI can surface the backend's specific URL
// validation message inline, mirroring updateAppDenyListThunk.
export const updateBrowserUrlDenyListThunk = createAsyncThunk(
  'user/updateBrowserUrlDenyList',
  async (data: UpdateEnterpriseSettingsRequest, { rejectWithValue }) => {
    try {
      const response = await updateEnterpriseSettings(data)
      return response.data
    } catch (error) {
      console.error('Failed to update browser URL deny list', error)
      return rejectWithValue(isApiError(error) ? error.message : "Couldn't update blocked URLs. Try again.")
    }
  }
)

/**
 * Rejection payload for the blocked-domains save. Structured rather than a bare
 * string because the 409 self-block is recoverable: the UI has to name the
 * offending entries and resend with `acknowledgeSelfBlock`.
 */
export interface NotetakerBlockedDomainsError {
  message: string
  /** `notetaker_blocked_domains_self_block` on the recoverable 409. */
  code?: string
  /** Entries that would also block the org's own members. */
  conflicts: string[]
}

const NOTETAKER_SELF_BLOCK_CODE = 'notetaker_blocked_domains_self_block'

function readConflicts(detailData: Record<string, unknown> | undefined): string[] {
  const raw = detailData?.conflicts
  return Array.isArray(raw) ? raw.filter((entry): entry is string => typeof entry === 'string') : []
}

// Distinct thunk so the admin UI can surface the backend's per-entry 400 inline and
// drive the 409 self-block confirmation, mirroring updateAppDenyListThunk.
export const updateNotetakerBlockedDomainsThunk = createAsyncThunk<
  EnterpriseSettings,
  UpdateEnterpriseSettingsRequest,
  { rejectValue: NotetakerBlockedDomainsError }
>('user/updateNotetakerBlockedDomains', async (data, { rejectWithValue }) => {
  try {
    const response = await updateEnterpriseSettings(data)
    return response.data
  } catch (error) {
    // No error object: `captureConsoleIntegration` ships console.error to Sentry, which would
    // both undo `expectedStatuses: [409]` and put the admin's raw entry text — a third party's
    // email address on the 400 path — into an issue attributed to them.
    console.error('Failed to update notetaker blocked domains', isApiError(error) ? error.status : 'unknown')
    if (!isApiError(error)) {
      return rejectWithValue({ message: "Couldn't update blocked domains. Try again.", conflicts: [] })
    }
    return rejectWithValue({
      message: error.message,
      // Fall back to the status: a 409 from this route is always the self-block guard,
      // so the confirmation still works if the body ever ships without the code.
      code: error.code ?? (error.status === 409 ? NOTETAKER_SELF_BLOCK_CODE : undefined),
      conflicts: readConflicts(error.detailData),
    })
  }
})

// Distinct thunk so the admin UI can surface the backend's specific retention
// rejection inline (e.g. the 400 when Cloud Sync is off), mirroring
// updateAppDenyListThunk. No pending/rejected reducers — rollback is
// component-side, so this never drives the global isSaveLoading.
export const updateNotetakerRetentionThunk = createAsyncThunk(
  'user/updateNotetakerRetention',
  async (data: UpdateEnterpriseSettingsRequest, { rejectWithValue }) => {
    try {
      const response = await updateEnterpriseSettings(data)
      return response.data
    } catch (error) {
      console.error('Failed to update notetaker transcript retention', error)
      return rejectWithValue(isApiError(error) ? error.message : "Couldn't update transcript retention. Try again.")
    }
  }
)

export const switchEnterprisePlanThunk = createAsyncThunk(
  'user/switchEnterprisePlan',
  async (data: SwitchPlanRequest, { rejectWithValue }) => {
    try {
      const response = await switchEnterprisePlan(data)
      return response.data
    } catch (error) {
      console.error('Failed to switch plan', error)
      // A 400's detail says WHY (e.g. "contact sales"); anything else stays generic.
      const detail = isApiError(error) && error.status === 400 && error.detail ? error.detail : undefined
      return rejectWithValue(detail ?? 'Failed to switch plan')
    }
  }
)

export const connectSSOThunk = createAsyncThunk('user/connectSSO', async (_, { rejectWithValue }) => {
  try {
    const res = await connectSSO()
    return res.data as ConnectSSOResponse
  } catch (error) {
    const apiError = error as ApiError
    if (apiError.status === 400 && isSsoInactiveResponse(apiError)) {
      return {
        message: apiError.message,
        detail: apiError.detail ?? undefined,
      } satisfies ConnectSSOResponse
    }
    if (apiError.status === 400) {
      return rejectWithValue('Please complete SSO setup first')
    } else {
      return rejectWithValue('Unable to connect to your SSO provider. Check your SSO configuration and try again.')
    }
  }
})

export const setupSSOThunk = createAsyncThunk('user/setupSSO', async (_, { rejectWithValue }) => {
  try {
    const res = await setupSSO()
    const url = cleanPortalUrl(res.data.sso_admin_portal_url)
    window.open(url, '_blank', 'noopener,noreferrer')
    return res.data
  } catch {
    return rejectWithValue('Failed to set up SSO. Please try again.')
  }
})

export const setupSCIMThunk = createAsyncThunk('user/setupSCIM', async (_, { rejectWithValue }) => {
  try {
    const res = await setupSCIM()
    const url = cleanPortalUrl(res.data.sso_admin_portal_url)
    window.open(url, '_blank', 'noopener,noreferrer')
    return res.data
  } catch {
    return rejectWithValue('Failed to set up SCIM. Please try again.')
  }
})

export const getSubscriptionThunk = createAsyncThunk('user/getSubscription', async (_, { rejectWithValue }) => {
  try {
    const response = await getSubscription()
    return response.data
  } catch (error) {
    const message = 'Failed to get subscription'
    console.error(message, error)
    return rejectWithValue(message)
  }
})

export const getNextInvoiceThunk = createAsyncThunk('user/getNextInvoice', async (_, { rejectWithValue }) => {
  try {
    const response = await getNextInvoice()
    return response.data
  } catch (error) {
    const message = 'Failed to get next invoice'
    console.error(message, error)
    return rejectWithValue(message)
  }
})

export const getMySeatInfoThunk = createAsyncThunk('user/getMySeatInfo', async (_, { rejectWithValue }) => {
  try {
    const response = await getMySeatInfo()
    return response.data
  } catch (error) {
    const message = 'Failed to get seat info'
    console.error(message, error)
    return rejectWithValue(message)
  }
})

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<Partial<Omit<UserState, TenantField>>>) => {
      return { ...state, ...action.payload }
    },
    setEnterprise: (state, action: PayloadAction<Enterprise | null>) => {
      state.enterprise = action.payload
    },
    updateProfilePic: (state, action: PayloadAction<{ avatarUrl?: string }>) => {
      return { ...state, ...action.payload }
    },
    clearUser: () => initialState,
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    setEmail: (state, action: PayloadAction<string>) => {
      state.email = action.payload
    },
    setName: (state, action: PayloadAction<string>) => {
      state.name = action.payload
    },
    setSSOState: (
      state,
      action: PayloadAction<{
        ssoProvider?: string | null
        isSSOLoading?: boolean
        isSSOConnected?: boolean
        isSSOValidating?: boolean
      }>
    ) => {
      return { ...state, ...action.payload }
    },
    resetSSOState: (state) => {
      state.ssoProvider = null
      state.isSSOLoading = false
      state.isSSOConnected = false
      state.isSSOValidating = false
    },
    clearEnterpriseData: (state) => {
      state.enterprise = null
      state.enterpriseLoaded = false
      state.enterpriseError = false
      state.subscription = null
      state.isLoading = false
      state.nextInvoice = null
      state.isNextInvoiceLoading = false
      state.seatInfo = null
      state.isSeatInfoLoading = false
      state.ssoProvider = null
      state.isSSOConnected = false
      state.isSSOLoading = false
      state.isSSOValidating = false
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateEnterpriseThunk.fulfilled, (state, action: PayloadAction<UpdateEnterpriseResponse>) => {
        state.isLoading = false
        state.enterprise!.name = action.payload.name
        state.enterprise!.logo = action.payload.logo
      })
      .addCase(updateEnterpriseThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateEnterpriseThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(updateEnterpriseSettingsThunk.fulfilled, (state, action: PayloadAction<EnterpriseSettings>) => {
        state.isLoading = false
        if (!state.enterprise) return
        state.enterprise.settings = action.payload
      })
      .addCase(updateEnterpriseSettingsThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(updateEnterpriseSettingsThunk.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(updateAppDenyListThunk.fulfilled, (state, action: PayloadAction<EnterpriseSettings>) => {
        if (!state.enterprise) return
        state.enterprise.settings = action.payload
      })
      .addCase(updateBrowserUrlDenyListThunk.fulfilled, (state, action: PayloadAction<EnterpriseSettings>) => {
        if (!state.enterprise) return
        state.enterprise.settings = action.payload
      })
      .addCase(updateNotetakerRetentionThunk.fulfilled, (state, action: PayloadAction<EnterpriseSettings>) => {
        if (!state.enterprise) return
        state.enterprise.settings = action.payload
      })
      .addCase(updateNotetakerBlockedDomainsThunk.fulfilled, (state, action: PayloadAction<EnterpriseSettings>) => {
        if (!state.enterprise) return
        state.enterprise.settings = action.payload
      })

    builder
      .addCase(createEnterpriseThunk.fulfilled, (state, action: PayloadAction<Enterprise>) => {
        state.isLoading = false
        state.enterprise = action.payload
        state.enterpriseLoaded = true
        state.enterpriseError = false
      })
      .addCase(createEnterpriseThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(createEnterpriseThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(getEnterpriseThunk.fulfilled, (state, action: PayloadAction<Enterprise>) => {
        state.isLoading = false
        state.enterprise = action.payload
        state.enterpriseLoaded = true
        state.enterpriseError = false
      })
      .addCase(getEnterpriseThunk.pending, (state) => {
        state.isLoading = true
        state.enterpriseError = false
      })
      .addCase(getEnterpriseThunk.rejected, (state) => {
        state.isLoading = false
        // Settle the gate on failure so AuthCheck doesn't hang, but flag the
        // error so the redirect logic can distinguish "no enterprise" from
        // "we don't know yet" and avoid misrouting to /get-started.
        state.enterpriseLoaded = true
        state.enterpriseError = true
      })

    builder
      .addCase(switchEnterprisePlanThunk.fulfilled, (state, action: PayloadAction<EnterpriseSubscription>) => {
        state.isLoading = false
        if (state.enterprise) {
          state.enterprise.subscription = action.payload
        }
      })
      .addCase(switchEnterprisePlanThunk.pending, (state) => {
        state.isLoading = true
      })
      .addCase(switchEnterprisePlanThunk.rejected, (state) => {
        state.isLoading = false
      })

    builder
      .addCase(connectSSOThunk.pending, (state) => {
        state.isSSOLoading = true
        state.isSSOConnected = false
        state.isSSOValidating = false
        state.ssoProvider = null
      })
      .addCase(connectSSOThunk.fulfilled, (state, action) => {
        state.isSSOLoading = false
        state.isSSOConnected = !!action.payload.provider
        state.isSSOValidating = !action.payload.provider
        state.ssoProvider = action.payload.provider ?? null
      })
      .addCase(connectSSOThunk.rejected, (state) => {
        state.isSSOLoading = false
        state.isSSOConnected = false
        state.isSSOValidating = false
        state.ssoProvider = null
      })

    builder
      .addCase(setupSSOThunk.pending, () => {
        // No state changes needed for SSO setup
      })
      .addCase(setupSSOThunk.fulfilled, () => {
        // No state changes needed for SSO setup
      })
      .addCase(setupSSOThunk.rejected, () => {
        // No state changes needed for SSO setup
      })

    builder
      .addCase(setupSCIMThunk.pending, () => {
        // No state changes needed for SCIM setup
      })
      .addCase(setupSCIMThunk.fulfilled, () => {
        // No state changes needed for SCIM setup
      })
      .addCase(setupSCIMThunk.rejected, () => {
        // No state changes needed for SCIM setup
      })

    builder.addCase(getSubscriptionThunk.fulfilled, (state, action: PayloadAction<SubscriptionResponse>) => {
      state.subscription = action.payload
    })

    builder
      .addCase(getNextInvoiceThunk.pending, (state) => {
        state.isNextInvoiceLoading = true
      })
      .addCase(getNextInvoiceThunk.fulfilled, (state, action: PayloadAction<NextInvoiceResponse>) => {
        state.isNextInvoiceLoading = false
        state.nextInvoice = action.payload
      })
      .addCase(getNextInvoiceThunk.rejected, (state) => {
        state.isNextInvoiceLoading = false
      })

    builder
      .addCase(getMySeatInfoThunk.pending, (state) => {
        state.isSeatInfoLoading = true
      })
      .addCase(getMySeatInfoThunk.fulfilled, (state, action: PayloadAction<MySeatInfoResponse>) => {
        state.isSeatInfoLoading = false
        state.seatInfo = action.payload
      })
      .addCase(getMySeatInfoThunk.rejected, (state) => {
        state.isSeatInfoLoading = false
      })
      .addCase(getSubscriptionThunk.pending, () => {
        // No loading state needed for subscription
      })
      .addCase(getSubscriptionThunk.rejected, () => {
        // No error handling needed for subscription
      })
  },
})

// Export actions
export const {
  setUser,
  setEnterprise,
  updateProfilePic,
  clearUser,
  setLoading,
  setEmail,
  setName,
  setSSOState,
  resetSSOState,
  clearEnterpriseData,
} = userSlice.actions

// Export selectors
export const selectUser = (state: RootState) => state.user
export const selectCurrentUserId = (state: RootState) => state.user.userId
export const selectCurrentEnterprise = (state: RootState): Enterprise | null => state.user.enterprise
export const selectEnterpriseLoaded = (state: RootState): boolean => state.user.enterpriseLoaded
export const selectEnterpriseError = (state: RootState): boolean => state.user.enterpriseError
export const selectEmail = (state: RootState) => state.user.email
export const selectName = (state: RootState) => state.user.name
export const selectIsLoading = (state: RootState) => state.user.isLoading
export const selectIsAdmin = (state: RootState) => isAdminRole(state.user.enterprise?.userRole)
export const selectIsSuperAdmin = (state: RootState) =>
  state.user.enterprise?.userRole?.toLocaleLowerCase() === 'superadmin'
export const selectIsWisprInternal = (state: RootState) => Boolean(state.user.email?.endsWith('@wispr.ai'))
export const selectIsInternalAdmin = (state: RootState) =>
  selectIsAdmin(state) && state.user.email?.endsWith('@wispr.ai')
export const selectIsInternalSuperAdmin = (state: RootState) =>
  selectIsSuperAdmin(state) && state.user.email?.endsWith('@wispr.ai')
export const selectPlanType = (state: RootState): PlanType | null => {
  if (!state.user.enterprise?.subscription?.productName) return null
  return getPlanTypeFromProductName(state.user.enterprise.subscription.productName)
}

// Sales-owned orgs are billed in Salesforce: no Stripe customer, invoices or self-serve plan changes.
export const selectIsSalesOwned = (state: RootState): boolean =>
  state.user.enterprise?.subscription?.billingProvider === BillingProvider.SalesOwned

export const selectPaymentSchedule = (state: RootState): PaymentSchedule | null => {
  if (!state.user.enterprise?.subscription?.productName) {
    return null
  }
  if (
    ['FLOW_TEAM_MONTHLY', 'FLOW_PRO_MONTHLY', 'FLOW_BUSINESS_MONTHLY'].includes(
      state.user.enterprise?.subscription?.productName
    )
  ) {
    return PaymentSchedule.Monthly
  }
  if (
    ['FLOW_TEAM_YEARLY', 'FLOW_PRO_YEARLY', 'FLOW_BUSINESS_YEARLY'].includes(
      state.user.enterprise?.subscription?.productName
    )
  ) {
    return PaymentSchedule.Annual
  }
  return null
}

export const selectHasActiveEnterpriseSubscription = (state: RootState): boolean => {
  return (
    state.user.enterprise?.subscription !== null &&
    (state.user.enterprise?.subscription?.status === SubscriptionStatus.Active ||
      state.user.enterprise?.subscription?.status === SubscriptionStatus.PastDue)
  )
}

export const selectHasActiveOrTrialingEnterpriseSubscription = (state: RootState): boolean => {
  return (
    state.user.enterprise?.subscription !== null &&
    ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES.includes(state.user.enterprise?.subscription?.status ?? '')
  )
}

// SSO selectors
export const selectSSOProvider = (state: RootState) => state.user.ssoProvider
export const selectIsSSOLoading = (state: RootState) => state.user.isSSOLoading
export const selectIsSSOConnected = (state: RootState) => state.user.isSSOConnected
export const selectIsSSOValidating = (state: RootState) => state.user.isSSOValidating

// SCIM selectors
export const selectIsScimEnabled = (state: RootState) => state.user.enterprise?.scimEnabled ?? false

// Subscription selectors
export const selectSubscription = (state: RootState) => state.user.subscription
export const selectTrialEndsAt = (state: RootState) => state.user.subscription?.trial_ends_at
export const selectDaysLeft = (state: RootState) =>
  state.user.subscription?.trial_ends_at ? calculateTrialDays(state.user.subscription?.trial_ends_at) : 0
export const selectSubscriptionStatus = (state: RootState) => state.user.subscription?.status
export const selectIsSubscribed = (state: RootState) => state.user.subscription?.is_subscribed
export const selectCredits = (state: RootState) => state.user.subscription?.credits
export const selectUserPlanType = (state: RootState) =>
  state.user.subscription ? getPlanTypeFromProductName(state.user.subscription.plan) : null

// Post-trial grace window. Backend keeps `grace_period_ends_at` populated for the whole window and resets `trial_ends_at` to 0 once the trial ends; helpers in `utils/subscription.ts` combine both to tell "trialing" apart from "in grace".
export const selectGracePeriodEndsAt = (state: RootState): number | null =>
  state.user.subscription?.grace_period_ends_at ?? null
// Backend ships `grace_period_ends_at` for any user with a `trial_ends_at`, even after they checkout — the active/trialing-sub guard belongs in the selector itself, otherwise every new consumer has to remember it (this is what bit us in #7274).
export const selectIsInGracePeriod = (state: RootState): boolean =>
  isInGracePeriod(state.user.subscription?.trial_ends_at, state.user.subscription?.grace_period_ends_at) &&
  !selectHasActiveOrTrialingEnterpriseSubscription(state)
export const selectGracePeriodDaysLeft = (state: RootState): number =>
  gracePeriodDaysLeft(state.user.subscription?.trial_ends_at, state.user.subscription?.grace_period_ends_at)
export const selectShouldShowGracePeriodModal = (state: RootState): boolean =>
  selectIsAdmin(state) && selectIsInGracePeriod(state)

// Next invoice selectors
export const selectNextInvoice = (state: RootState) => state.user.nextInvoice
export const selectIsNextInvoiceLoading = (state: RootState) => state.user.isNextInvoiceLoading

// Seat info selectors
export const selectSeatInfo = (state: RootState) => state.user.seatInfo
export const selectIsSeatInfoLoading = (state: RootState) => state.user.isSeatInfoLoading
export const selectSeatType = (state: RootState) => state.user.seatInfo?.seat_type
export const selectPlanTypeFromSeat = (state: RootState) => state.user.seatInfo?.plan_type

// Export reducer
export default userSlice.reducer
